import type { FileValidationError, FileValidator } from '@/shared/utils/upload-utils';
import { z } from 'zod';

const SEPARATOR = ';';
const REQUIRED_COLUMNS = ['code', 'libelle'] as const;
const ERROR_KEY_PREFIX = 'admin.thesaurus.import.error';

/** Nom de colonne comparable : espaces et guillemets retirés, en minuscules. */
const normalizeColumnName = (value: string): string =>
  value
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .trim()
    .toLowerCase();

/** Valeur de cellule comparable : espaces et guillemets englobants retirés. */
const normalizeCell = (value: string | undefined): string =>
  (value ?? '')
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .trim();

/** Structure du CSV une fois découpé : en-tête normalisé et lignes de données numérotées. */
const thesaurusCsvSchema = z
  .object({
    header: z.array(z.string()),
    rows: z.array(z.object({ line: z.number(), cells: z.array(z.string()) })),
  })
  .superRefine(({ header, rows }, ctx) => {
    const missingColumns = REQUIRED_COLUMNS.filter(column => !header.includes(column));

    missingColumns.forEach(column => {
      ctx.addIssue({
        code: 'custom',
        message: `${ERROR_KEY_PREFIX}.missing-column`,
        params: { column },
      });
    });

    if (rows.length === 0) {
      ctx.addIssue({ code: 'custom', message: `${ERROR_KEY_PREFIX}.empty` });
    }

    if (missingColumns.length > 0) {
      return;
    }

    const codeIndex = header.indexOf('code');
    const labelIndex = header.indexOf('libelle');
    const firstLineByCode = new Map<string, number>();

    rows.forEach(({ line, cells }) => {
      const code = normalizeCell(cells[codeIndex]);

      if (code === '') {
        ctx.addIssue({
          code: 'custom',
          message: `${ERROR_KEY_PREFIX}.empty-code`,
          params: { line },
        });
      } else {
        const firstLine = firstLineByCode.get(code);

        if (firstLine === undefined) {
          firstLineByCode.set(code, line);
        } else {
          ctx.addIssue({
            code: 'custom',
            message: `${ERROR_KEY_PREFIX}.duplicate-code`,
            params: { code, line, firstLine },
          });
        }
      }

      if (normalizeCell(cells[labelIndex]) === '') {
        ctx.addIssue({
          code: 'custom',
          message: `${ERROR_KEY_PREFIX}.empty-label`,
          params: { line },
        });
      }
    });
  });

/** Décode le fichier en UTF-8 strict ; `null` si l'encodage est invalide. */
const decodeUtf8 = async (file: File): Promise<string | null> => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer());
  } catch {
    return null;
  }
};

const toValidationError = (issue: z.core.$ZodIssue): FileValidationError => ({
  key: issue.message,
  params: issue.code === 'custom' ? issue.params : undefined,
});

/**
 * Contrôles du fichier d'import d'un thésaurus : encodage UTF-8, séparateur `;`,
 * colonnes `code` et `libelle` présentes, au moins une ligne de données, codes uniques
 * et ni code ni libellé vide.
 */
export const validateThesaurusImportFile: FileValidator = async file => {
  const content = await decodeUtf8(file);

  if (content === null) {
    return [{ key: `${ERROR_KEY_PREFIX}.encoding` }];
  }

  // Les lignes vides sont ignorées, mais leur numéro d'origine est conservé pour les messages
  const lines = content
    .split(/\r?\n/)
    .map((text, index) => ({ text, line: index + 1 }))
    .filter(({ text }) => text.trim() !== '');
  const [header, ...dataLines] = lines;
  const headerLine = header?.text;

  if (!headerLine) {
    return [{ key: `${ERROR_KEY_PREFIX}.empty` }];
  }

  if (!headerLine.includes(SEPARATOR)) {
    return [{ key: `${ERROR_KEY_PREFIX}.separator` }];
  }

  const result = thesaurusCsvSchema.safeParse({
    header: headerLine.split(SEPARATOR).map(normalizeColumnName),
    rows: dataLines.map(({ text, line }) => ({ line, cells: text.split(SEPARATOR) })),
  });

  return result.success ? [] : result.error.issues.map(toValidationError);
};
